/**
 * Client-Side Form Validation Utilities
 *
 * Task: T-P4-D2-F4b-2
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Provides client-side validation for form fields. This validation is for UX only
 * (instant feedback) - the server is authoritative and will re-validate on submit.
 *
 * Validation patterns mirror the backend FormValidationService at:
 * apps/api/src/modules/webapp/services/form-validation.service.ts
 */

import type { FormField, FieldError } from '../types/forms';
import { evaluateShowWhen } from '@telify/shared';

/**
 * Validate a single field value against its field definition.
 *
 * @param field - Field definition with type and constraints
 * @param value - Current field value
 * @returns Error message string if invalid, null if valid
 */
export function validateField(
  field: FormField,
  value: unknown
): string | null {
  // CONTENT fields are non-interactive — skip all validation including required
  if (field.type === 'CONTENT') return null;

  // Required check - must come first
  if (field.required && isEmpty(value)) {
    return 'This field is required';
  }

  // Skip validation for optional empty fields
  if (isEmpty(value) && !field.required) {
    return null;
  }

  // Type-specific validation
  switch (field.type) {
    case 'TEXT':
    case 'TEXTAREA':
      return validateText(field, value);
    case 'EMAIL':
      return validateEmail(field, value);
    case 'NUMBER':
      return validateNumber(field, value);
    case 'DATE':
      return validateDate(field, value);
    case 'SELECT':
    case 'RADIO':
      return validateSelection(field, value);
    case 'CHECKBOX':
      return validateCheckbox(field, value);
    case 'RATING':
      return validateRating(value);
    case 'SIGNATURE':
      return validateSignature(value);
    default:
      return null;
  }
}

/**
 * Check if a value is empty.
 * Treats undefined, null, empty string, and whitespace-only as empty.
 *
 * Note: Arrays and booleans are NOT considered empty here.
 * - Empty arrays for multi-select checkboxes are handled by validateCheckbox
 * - False for consent checkboxes is handled by validateCheckbox
 */
function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  // Arrays are NOT considered empty - let checkbox validation handle this
  // with a more specific error message
  // Booleans are NOT considered empty - let checkbox validation handle this
  return false;
}

/**
 * Validate TEXT and TEXTAREA fields.
 * Checks minLength, maxLength, and pattern constraints.
 */
function validateText(field: FormField, value: unknown): string | null {
  if (typeof value !== 'string') {
    return 'Invalid value';
  }

  const trimmed = value.trim();

  if (field.minLength != null && trimmed.length < field.minLength) {
    return `Must be at least ${field.minLength} characters`;
  }

  if (field.maxLength != null && trimmed.length > field.maxLength) {
    return `Must be at most ${field.maxLength} characters`;
  }

  if (field.pattern) {
    try {
      const regex = new RegExp(field.pattern, 'u');
      if (!regex.test(value)) {
        return field.patternMessage || 'Invalid format';
      }
    } catch {
      // Invalid regex pattern - skip validation (backend will catch)
      return null;
    }
  }

  return null;
}

/**
 * Validate EMAIL fields.
 * Uses a standard email regex pattern.
 */
function validateEmail(field: FormField, value: unknown): string | null {
  if (typeof value !== 'string') {
    return 'Invalid value';
  }

  // Standard email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return 'Please enter a valid email address';
  }

  // Also check maxLength if specified
  if (field.maxLength != null && value.length > field.maxLength) {
    return `Must be at most ${field.maxLength} characters`;
  }

  return null;
}

/**
 * Validate NUMBER fields.
 * Parses string/number values and checks min/max bounds.
 */
function validateNumber(field: FormField, value: unknown): string | null {
  // Accept both string and number (input elements return strings)
  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (typeof num !== 'number' || !Number.isFinite(num)) {
    return 'Must be a valid number';
  }

  if (field.min != null && num < field.min) {
    return `Must be at least ${field.min}`;
  }

  if (field.max != null && num > field.max) {
    return `Must be at most ${field.max}`;
  }

  return null;
}

/**
 * Render a DATE bound (epoch ms of UTC midnight) as human-readable copy,
 * e.g. 'Sep 3, 2026'. Forced to UTC so the message never shifts by browser timezone.
 */
function formatBound(epochMs: number): string {
  return new Date(epochMs).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Validate DATE fields.
 * Checks for YYYY-MM-DD format, valid date, and min/max bounds.
 * `field.min` / `field.max` are epoch ms of the UTC midnight for a bound day.
 */
function validateDate(field: FormField, value: unknown): string | null {
  if (typeof value !== 'string') {
    return 'Invalid value';
  }

  // Format check: YYYY-MM-DD
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(value)) {
    return 'Invalid date format (use YYYY-MM-DD)';
  }

  // Validity check (catches 2024-02-30 etc.)
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  // Additional check: ensure the parsed date matches the input
  // (catches edge cases like month overflow)
  const [year, month, day] = value.split('-').map(Number);
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() + 1 !== month ||
    date.getUTCDate() !== day
  ) {
    return 'Invalid date';
  }

  // Bounds check: `date` is already the submitted day at UTC midnight (ISO date-only strings parse as UTC)
  const epoch = date.getTime();
  if (field.min != null && epoch < field.min) {
    return `Must be on or after ${formatBound(field.min)}`;
  }
  if (field.max != null && epoch > field.max) {
    return `Must be on or before ${formatBound(field.max)}`;
  }

  return null;
}

/**
 * Validate SELECT and RADIO fields.
 * Ensures selected value is one of the defined options.
 */
function validateSelection(field: FormField, value: unknown): string | null {
  if (typeof value !== 'string') {
    return 'Invalid value';
  }

  if (!field.options || field.options.length === 0) {
    // No options defined - can't validate (backend will handle)
    return null;
  }

  if (!field.options.some((o) => o.value === value)) {
    return 'Invalid selection';
  }

  return null;
}

/**
 * Validate CHECKBOX fields.
 *
 * Two modes:
 * - Consent mode (no options): value is boolean, required means must be true
 * - Multi-select mode (with options): value is string[], validates option values
 */
function validateCheckbox(field: FormField, value: unknown): string | null {
  // Multi-select mode: options present
  if (field.options && field.options.length > 0) {
    if (!Array.isArray(value)) {
      return 'Invalid value';
    }

    // Validate each selected value is a valid option
    const validValues = new Set(field.options.map((o) => o.value));
    for (const selected of value) {
      if (typeof selected !== 'string' || !validValues.has(selected)) {
        return 'Invalid selection';
      }
    }

    // Required multi-select must have at least one selection
    if (field.required && value.length === 0) {
      return 'Please select at least one option';
    }

    return null;
  }

  // Consent mode: no options, value is boolean
  if (typeof value !== 'boolean') {
    return 'Invalid checkbox value';
  }

  // Required consent checkbox must be checked (true)
  if (field.required && value !== true) {
    return 'This field is required';
  }

  return null;
}

/**
 * Validate RATING fields.
 * Must be an integer between 1 and 5.
 */
function validateRating(value: unknown): string | null {
  if (typeof value !== 'number') {
    return 'Please select a rating';
  }
  if (!Number.isInteger(value) || value < 1 || value > 5) {
    return 'Please select a rating between 1 and 5';
  }
  return null;
}

/**
 * Validate SIGNATURE fields.
 * Must be a PNG data URI within 200KB decoded size.
 */
function validateSignature(value: unknown): string | null {
  if (typeof value !== 'string') {
    return 'Please provide a signature';
  }
  const PREFIX = 'data:image/png;base64,';
  if (!value.startsWith(PREFIX)) {
    return 'Invalid signature format';
  }
  const base64Payload = value.slice(PREFIX.length);
  const estimatedBytes = Math.ceil(base64Payload.length * 3 / 4);
  if (estimatedBytes > 204800) {
    return 'Signature image exceeds 200KB limit';
  }
  return null;
}

/**
 * Compute the set of visible field IDs based on showWhen conditions.
 * Uses iterative stabilization to handle chained dependencies.
 */
export function computeVisibleFieldIds(
  fields: FormField[],
  values: Record<string, unknown>
): Set<string> {
  const fieldMap = new Map(fields.map((f) => [f.fieldId, f]));
  const visible = new Set<string>();

  // Fields without showWhen are always visible
  for (const field of fields) {
    if (!field.showWhen) {
      visible.add(field.fieldId);
    }
  }

  // Iterative stabilization for chained deps
  const maxIterations = fields.length;
  for (let i = 0; i < maxIterations; i++) {
    let changed = false;
    for (const field of fields) {
      if (!field.showWhen) continue;
      const sourceField = fieldMap.get(field.showWhen.fieldId);
      if (!sourceField) {
        // Source not found — fail-closed (hidden)
        if (visible.delete(field.fieldId)) changed = true;
        continue;
      }
      if (!visible.has(sourceField.fieldId)) {
        // Source is hidden — dependent is also hidden
        if (visible.delete(field.fieldId)) changed = true;
        continue;
      }
      const sourceValue = values[field.showWhen.fieldId];
      const isVisible = evaluateShowWhen(field.showWhen, sourceValue, sourceField);
      if (isVisible && !visible.has(field.fieldId)) {
        visible.add(field.fieldId);
        changed = true;
      } else if (!isVisible && visible.has(field.fieldId)) {
        visible.delete(field.fieldId);
        changed = true;
      }
    }
    if (!changed) break;
  }

  return visible;
}

/**
 * Validate all fields in a form.
 *
 * @param fields - Array of field definitions
 * @param values - Object mapping fieldId to current value
 * @returns Array of field errors (empty if all valid)
 */
export function validateAllFields(
  fields: FormField[],
  values: Record<string, unknown>
): FieldError[] {
  const errors: FieldError[] = [];
  const visibleFieldIds = computeVisibleFieldIds(fields, values);

  for (const field of fields) {
    if (!visibleFieldIds.has(field.fieldId)) continue;
    const error = validateField(field, values[field.fieldId]);
    if (error) {
      errors.push({ fieldId: field.fieldId, error });
    }
  }

  return errors;
}

/**
 * Check if a form has any validation errors.
 *
 * @param fields - Array of field definitions
 * @param values - Object mapping fieldId to current value
 * @returns true if form is valid (no errors)
 */
export function isFormValid(
  fields: FormField[],
  values: Record<string, unknown>
): boolean {
  return validateAllFields(fields, values).length === 0;
}
